import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { elementColor } from './elements';
import { useAnimationFrame } from './useAnimationFrame';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  VIZ_BTN_GHOST,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from './vizShared';

// ParticleView — animated SVG particle model of matter. Modes:
//   'element'  — identical atoms drifting
//   'compound' — bonded molecules (e.g. H2O) drifting together
//   'mixture'  — two kinds of atoms intermixed. The optional `uniformity` field
//                refines this into the homogeneous-vs-heterogeneous contrast:
//                  'homogeneous'   — species dispersed EVENLY so every region
//                                    looks the same (one uniform phase)
//                  'heterogeneous' — each species CLUSTERED into its own region
//                                    so the parts are visibly distinct
//                Omitted → the original intermixed (shuffled) mixture look.
//   'states'   — solid (vibrating lattice) / liquid (loose flow) / gas (fast
//                bounce); a control switches state and the motion re-animates
// config: { mode, atoms?:[{symbol,color,count}], molecule?, state?, uniformity?, title? }
// Resilient: missing config → a sensible default element view.

type Mode = 'element' | 'compound' | 'mixture' | 'states' | 'dissolving';
type StateName = 'solid' | 'liquid' | 'gas';
type Uniformity = 'homogeneous' | 'heterogeneous';

export interface ParticleViewConfig {
  title?: string;
  mode?: Mode;
  atoms?: Array<{ symbol?: string; color?: string; count?: number }>;
  // For compound mode: simple molecule recipes. "H2O" supported by name, or pass
  // explicit atoms with the first as the central atom.
  molecule?: string;
  state?: StateName;
  // For mixture (and dissolving) mode: contrast uniform single-phase mixtures
  // against ones with visibly distinct regions. Omitted → classic intermixed.
  uniformity?: Uniformity;
}

interface Props {
  config?: ParticleViewConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const W = 320;
const H = 200;
const R = 9; // atom radius
const STATES: StateName[] = ['solid', 'liquid', 'gas'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  // home position (for solid lattice vibration)
  hx: number;
  hy: number;
  color: string;
  symbol: string;
  // molecule grouping (compound mode): satellites orbit a center index
  groupId?: number;
  isCenter?: boolean;
  angle?: number;
  dist?: number;
}

// Known compound recipes (center + satellites). Falls back to generic.
const MOLECULES: Record<string, { center: string; satellites: string[] }> = {
  H2O: { center: 'O', satellites: ['H', 'H'] },
  CO2: { center: 'C', satellites: ['O', 'O'] },
  NH3: { center: 'N', satellites: ['H', 'H', 'H'] },
  CH4: { center: 'C', satellites: ['H', 'H', 'H', 'H'] },
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function buildParticles(
  config: ParticleViewConfig,
  state: StateName,
  uniformity?: Uniformity,
): Particle[] {
  const mode = config.mode ?? 'element';
  const out: Particle[] = [];

  const addGrid = (count: number, make: (i: number, gx: number, gy: number) => Particle) => {
    const cols = Math.ceil(Math.sqrt(count * (W / H)));
    const rows = Math.ceil(count / cols);
    const cellW = W / (cols + 1);
    const cellH = H / (rows + 1);
    for (let i = 0; i < count; i++) {
      const c = i % cols;
      const r = Math.floor(i / cols);
      out.push(make(i, cellW * (c + 1), cellH * (r + 1)));
    }
  };

  if (mode === 'compound') {
    const recipe =
      (config.molecule && MOLECULES[config.molecule]) ||
      MOLECULES[(config.atoms?.[0]?.symbol ?? '') + ''] ||
      MOLECULES.H2O;
    const moleculeCount = 6;
    for (let g = 0; g < moleculeCount; g++) {
      const cx = rand(40, W - 40);
      const cy = rand(40, H - 40);
      const v = () => rand(-12, 12);
      const vx = v();
      const vy = v();
      const center: Particle = {
        x: cx, y: cy, vx, vy, hx: cx, hy: cy,
        color: elementColor(recipe.center), symbol: recipe.center,
        groupId: g, isCenter: true,
      };
      out.push(center);
      recipe.satellites.forEach((sym, k) => {
        const a = (k / recipe.satellites.length) * Math.PI * 2;
        out.push({
          x: cx, y: cy, vx, vy, hx: cx, hy: cy,
          color: elementColor(sym), symbol: sym,
          groupId: g, isCenter: false, angle: a, dist: R * 1.9,
        });
      });
    }
    return out;
  }

  // 'dissolving' renders like a solution: two kinds of particles intermixed
  // (solute dispersed through solvent). Shares the mixture layout.
  if (mode === 'mixture' || mode === 'dissolving') {
    const kinds = (config.atoms && config.atoms.length >= 2)
      ? config.atoms
      : [{ symbol: 'Na', count: 8 }, { symbol: 'Cl', count: 8 }];
    const total = kinds.reduce((s, k) => s + (k.count ?? 6), 0);
    let placed = 0;
    // Per-species pools (preserve which species each particle belongs to so the
    // homogeneous/heterogeneous layouts can interleave vs. cluster them).
    const pools: Array<Array<{ symbol: string; color: string }>> = kinds.map((k) => {
      const sym = k.symbol ?? 'X';
      const color = k.color ?? elementColor(sym);
      const n = Math.max(0, Math.floor(k.count ?? 6));
      return Array.from({ length: n }, () => ({ symbol: sym, color }));
    });
    const flat: Array<{ symbol: string; color: string }> = [];

    if (uniformity === 'heterogeneous') {
      // CLUSTER each species: fill the grid species-by-species so each kind
      // pools into its own contiguous region (visibly distinct parts). Grid
      // cells are emitted row-major, so contiguous indices form a coherent zone.
      pools.forEach((pool) => pool.forEach((p) => flat.push(p)));
    } else if (uniformity === 'homogeneous') {
      // DISPERSE evenly: round-robin across species so neighbouring cells keep
      // alternating — every region of the field has the same blend (one phase).
      let remaining = total;
      const cursors = pools.map(() => 0);
      while (remaining > 0) {
        for (let s = 0; s < pools.length && remaining > 0; s++) {
          if (cursors[s] < pools[s].length) {
            flat.push(pools[s][cursors[s]++]);
            remaining--;
          }
        }
      }
    } else {
      // Default mixture: shuffle so they intermix (original behaviour).
      pools.forEach((pool) => pool.forEach((p) => flat.push(p)));
      for (let i = flat.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [flat[i], flat[j]] = [flat[j], flat[i]];
      }
    }

    // Heterogeneous clusters should hold their shape, so particles barely
    // drift; even/default mixtures keep their lively wander.
    const vRange = uniformity === 'heterogeneous' ? 4 : 15;
    addGrid(total, (i, gx, gy) => {
      const f = flat[placed++] ?? flat[0] ?? { symbol: 'X', color: elementColor('X') };
      return {
        x: gx, y: gy,
        vx: rand(-vRange, vRange), vy: rand(-vRange, vRange),
        hx: gx, hy: gy, color: f.color, symbol: f.symbol,
      };
    });
    return out;
  }

  // 'element' and 'states' share an identical-atom layout.
  const spec = config.atoms?.[0];
  const symbol = spec?.symbol ?? (mode === 'states' ? 'Ar' : 'Ne');
  const color = spec?.color ?? elementColor(symbol);
  const count = spec?.count ?? (state === 'gas' ? 14 : 25);
  addGrid(count, (i, gx, gy) => ({
    x: gx, y: gy, vx: rand(-1, 1), vy: rand(-1, 1), hx: gx, hy: gy, color, symbol,
  }));
  return out;
}

export default function ParticleView({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const mode: Mode = config.mode ?? 'element';

  // Only mixture/dissolving honour a uniformity refinement; elsewhere it stays
  // undefined so every other mode keeps its exact current behaviour.
  const supportsUniformity = mode === 'mixture' || mode === 'dissolving';
  const initialUniformity: Uniformity | undefined = supportsUniformity ? config.uniformity : undefined;

  const [state, setState] = useState<StateName>(config.state ?? 'gas');
  const [uniformity, setUniformity] = useState<Uniformity | undefined>(initialUniformity);
  // Under reduced motion the loop is paused (static frame); otherwise plays.
  const [paused, setPaused] = useState(reducedMotion);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const demoActiveRef = useRef(false);

  // Rebuild the particle set when mode/state/uniformity changes.
  const particles = useMemo(
    () => buildParticles(config, state, uniformity),
    [config, state, uniformity],
  );
  // Mirror into refs the RAF loop mutates; React state holds a render tick.
  const partsRef = useRef<Particle[]>(particles);
  partsRef.current = particles;
  const [, force] = useState(0);

  // Speed factor by state.
  const speed = mode === 'states' ? (state === 'solid' ? 0 : state === 'liquid' ? 22 : 70) : 30;

  // --- Self-demo: cycle states (states mode) or just settle, then "Now you try".
  useEffect(() => {
    const demoEnabled = !reducedMotion && !cueOn;
    if (!demoEnabled) {
      setShowTry(false);
      return;
    }
    demoActiveRef.current = true;
    const timers: number[] = [];
    if (mode === 'states') {
      // Briefly cycle solid → liquid → gas (~0.8s each), then settle.
      const settle = config.state ?? 'gas';
      setState('solid');
      timers.push(window.setTimeout(() => { if (demoActiveRef.current) setState('liquid'); }, 800));
      timers.push(window.setTimeout(() => { if (demoActiveRef.current) setState('gas'); }, 1600));
      timers.push(window.setTimeout(() => {
        if (!demoActiveRef.current) return;
        setState(settle);
        demoActiveRef.current = false;
        setShowTry(true);
      }, 2400));
    } else if (supportsUniformity && initialUniformity != null) {
      // Mixture WITH an explicit uniformity: demo the contrast by flipping
      // homogeneous ⇄ heterogeneous, then settle on the configured one. A plain
      // mixture (no uniformity set) is left untouched in the `else` below.
      const settle: Uniformity = initialUniformity;
      setUniformity('homogeneous');
      timers.push(window.setTimeout(() => { if (demoActiveRef.current) setUniformity('heterogeneous'); }, 1100));
      timers.push(window.setTimeout(() => {
        if (!demoActiveRef.current) return;
        setUniformity(settle);
        demoActiveRef.current = false;
        setShowTry(true);
      }, 2200));
    } else {
      // Other modes: just let the entrance play, then cue.
      timers.push(window.setTimeout(() => {
        if (!demoActiveRef.current) return;
        demoActiveRef.current = false;
        setShowTry(true);
      }, 1200));
    }
    return () => {
      demoActiveRef.current = false;
      timers.forEach((t) => window.clearTimeout(t));
    };
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Cue: play/pause, state highlight, or step → state index. Supersedes demo.
  useEffect(() => {
    if (!cue) return;
    demoActiveRef.current = false;
    setShowTry(false);
    if (typeof cue.play === 'boolean') setPaused(!cue.play);
    if (mode === 'states') {
      const tokens = cueHighlights(cue);
      const named = tokens.find((t) => STATES.includes(t as StateName)) as StateName | undefined;
      if (named) setState(named);
      else if (typeof cue.step === 'number') {
        const idx = ((cue.step % STATES.length) + STATES.length) % STATES.length;
        setState(STATES[idx]);
      }
    } else if (supportsUniformity) {
      // Toggle the homogeneous↔heterogeneous contrast from narration. Accept
      // either highlight token (homogeneous/heterogeneous, or the shorthands
      // uniform/clustered) or step 0/1 (0 = homogeneous, 1 = heterogeneous).
      const tokens = cueHighlights(cue);
      let next: Uniformity | undefined;
      if (tokens.some((t) => t === 'heterogeneous' || t === 'clustered' || t === 'distinct')) {
        next = 'heterogeneous';
      } else if (tokens.some((t) => t === 'homogeneous' || t === 'uniform' || t === 'mixed')) {
        next = 'homogeneous';
      } else if (typeof cue.step === 'number') {
        next = cue.step % 2 === 0 ? 'homogeneous' : 'heterogeneous';
      }
      if (next) setUniformity(next);
    }
  }, [cue, mode, supportsUniformity]);

  // Under reduced motion, run one frame so particles are placed sensibly even
  // though the rAF loop is paused. Deliberately depends on `reducedMotion`
  // ONLY, never on `particles`: `partsRef.current = particles` above already
  // keeps the SVG in sync with the latest particles on every render, and
  // `config` defaults to a fresh `{}` when the caller omits it (true for the
  // empty-config live-activity path), so `particles` gets a new identity on
  // every render regardless of anything actually changing. Depending on it
  // here previously re-armed this effect every render — an infinite loop
  // under reduced motion (force → re-render → new particles ref → effect
  // fires → force → …) that pegs a CPU core and never repaints, which is
  // indistinguishable from "the widget doesn't play" to whoever is looking
  // at it.
  useEffect(() => {
    if (reducedMotion) force((n) => (n + 1) % 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion]);

  useAnimationFrame((dt) => {
    const parts = partsRef.current;
    for (const p of parts) {
      if (mode === 'states' && state === 'solid') {
        // Vibrate around home position (lattice).
        p.x = p.hx + Math.sin(performance.now() / 120 + p.hy) * 1.6;
        p.y = p.hy + Math.cos(performance.now() / 110 + p.hx) * 1.6;
        continue;
      }
      if (p.isCenter === false) continue; // satellites follow their center below
      const sp = speed;
      p.x += (p.vx / 30) * sp * dt;
      p.y += (p.vy / 30) * sp * dt;
      // Bounce off walls.
      if (p.x < R) { p.x = R; p.vx = Math.abs(p.vx); }
      if (p.x > W - R) { p.x = W - R; p.vx = -Math.abs(p.vx); }
      if (p.y < R) { p.y = R; p.vy = Math.abs(p.vy); }
      if (p.y > H - R) { p.y = H - R; p.vy = -Math.abs(p.vy); }
    }
    // Position satellites around their center (compound mode).
    if (mode === 'compound') {
      const centers = new Map<number, Particle>();
      for (const p of parts) if (p.isCenter && p.groupId != null) centers.set(p.groupId, p);
      for (const p of parts) {
        if (p.isCenter === false && p.groupId != null) {
          const c = centers.get(p.groupId);
          if (c) {
            p.angle = (p.angle ?? 0) + dt * 1.2;
            p.x = c.x + Math.cos(p.angle) * (p.dist ?? R * 1.9);
            p.y = c.y + Math.sin(p.angle) * (p.dist ?? R * 1.9);
          }
        }
      }
    }
    force((n) => (n + 1) % 1000);
  }, !paused && !reducedMotion);

  const markInteracted = () => {
    demoActiveRef.current = false;
    setShowTry(false);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'particle_view', mode });
    }
  };

  const changeState = (s: StateName) => {
    setState(s);
    markInteracted();
  };

  const changeUniformity = (u: Uniformity) => {
    setUniformity(u);
    markInteracted();
  };

  // Caption describing the current mixture uniformity (only when active).
  const uniformityCaption =
    uniformity === 'homogeneous'
      ? 'Homogeneous — uniform throughout'
      : uniformity === 'heterogeneous'
        ? 'Heterogeneous — visibly distinct regions'
        : null;

  return (
    <VizCard
      eyebrow="Particle model"
      title={config.title}
      reducedMotion={reducedMotion}
      hint={
        mode === 'states'
          ? 'Switch states to see how particle motion changes'
          : supportsUniformity
            ? 'Toggle homogeneous (uniform throughout) vs heterogeneous (visibly distinct regions)'
            : 'Play / pause the particle motion'
      }
    >
      <div className="relative w-full">
        <NowYouTry show={showTry} />
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          style={{ maxHeight: 240 }}
          role="img"
          aria-label={`Particle view: ${mode}`}
        >
          {/* Container hint for the 'states' mode */}
          {mode === 'states' && (
            <rect x="2" y="2" width={W - 4} height={H - 4} rx="6" fill="none" stroke="var(--ws-border)" strokeDasharray="4 4" />
          )}
          {partsRef.current.map((p, i) => (
            <g key={i}>
              {/* bond line for compound satellites */}
              {mode === 'compound' && p.isCenter === false && p.groupId != null && (() => {
                const c = partsRef.current.find((q) => q.isCenter && q.groupId === p.groupId);
                return c ? <line x1={c.x} y1={c.y} x2={p.x} y2={p.y} stroke="var(--ws-ink-4)" strokeWidth="2" /> : null;
              })()}
              <circle cx={p.x} cy={p.y} r={R} fill={p.color} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
              <text x={p.x} y={p.y + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill="#1A1A1A">
                {p.symbol}
              </text>
            </g>
          ))}
          {/* Caption banner for the homogeneous/heterogeneous contrast. */}
          {uniformityCaption && (
            <g>
              <rect x="0" y={H - 18} width={W} height="18" fill="rgba(0,0,0,0.55)" />
              <text
                x={W / 2}
                y={H - 5}
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                fill="#FFFFFF"
              >
                {uniformityCaption}
              </text>
            </g>
          )}
        </svg>

        {/* Controls */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {mode === 'states' && (
            <div className="flex overflow-hidden rounded-sm border border-ws-border">
              {STATES.map((s) => {
                const isActive = state === s;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => changeState(s)}
                    className={`px-3 py-1 text-xs font-medium capitalize transition-colors ${VIZ_FOCUS} ${
                      isActive
                        ? 'bg-ws-accent text-ws-on-accent'
                        : 'bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}
          {supportsUniformity && (
            <div className="flex overflow-hidden rounded-sm border border-ws-border">
              {(['homogeneous', 'heterogeneous'] as const).map((u) => {
                const isActive = uniformity === u;
                return (
                  <button
                    key={u}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => changeUniformity(u)}
                    className={`px-3 py-1 text-xs font-medium capitalize transition-colors ${VIZ_FOCUS} ${
                      isActive
                        ? 'bg-ws-accent text-ws-on-accent'
                        : 'bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                    }`}
                  >
                    {u}
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={() => { setPaused((p) => !p); markInteracted(); }}
            disabled={reducedMotion}
            className={`${VIZ_BTN_GHOST} !px-2.5 !py-1`}
            aria-label={paused ? 'Play animation' : 'Pause animation'}
            title={reducedMotion ? 'Motion is reduced by your system settings' : undefined}
          >
            {paused ? <Play size={12} /> : <Pause size={12} />}
            {paused ? 'Play' : 'Pause'}
          </button>
          <span className="text-[11px] text-ws-ink-3">
            {mode === 'states'
              ? `State: ${state}`
              : supportsUniformity && uniformity
                ? `Mixture: ${uniformity}`
                : `Mode: ${mode}`}
          </span>
        </div>
      </div>
    </VizCard>
  );
}
